/**
 * ADD / EDIT RECORD on DESKTOP — an MDM-console form (2026-09-26), replacing
 * the phone keypad screen squeezed into a 480px dialog.
 *
 *   ┌ New Record ───────────────────────────────────────────────── ✕ ┐
 *   │ Expense  Income  Transfer                                       │
 *   ├────────────────────────────────────────────┬────────────────────┤
 *   │ Amount *   : ₹ [ 450+120      ]             │ Same as last       │
 *   │ Date       : Today Yesterday [2026-09-26]   │ Recent             │
 *   │ Account *  : [ HDFC/Savings ▾ ]             │ Templates          │
 *   │ Category * : [search]  parents │ children   │ Most used          │
 *   │ Paid with  : [ UPI ▾ ]                      │                    │
 *   │ Remark *   : [                  ]           │                    │
 *   │ Labels     : chips                          │                    │
 *   ├────────────────────────────────────────────┴────────────────────┤
 *   │ hint                              Save & add another    Save    │
 *   └─────────────────────────────────────────────────────────────────┘
 *
 * Every rule lives in `useEntryForm` (shared with the phone screen): F7
 * remark + sub-category, F4 recent, F5 clone, templates, same-as-last,
 * duplicate, custom fields. This file only lays them out.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import {
  Check,
  ChevronLeft,
  ChevronRight,
  Copy,
  Link2,
  Plus,
  RotateCcw,
  Search,
  X,
} from "lucide-react";

import { AmountText } from "@/components/ui/AmountText";
import { cn } from "@/lib/utils";
import { TemplatePicker } from "@/components/templates";
import { useAuth } from "@/lib/auth/AuthProvider";
import { useHousehold } from "@/lib/household/useHousehold";
import { accountTileName } from "@/lib/accounts/tileName";

import { ExtraFields } from "./ExtraFields";
import { LabelChips } from "./LabelChips";
import { categoryPath, groupCategories, selectableCategories } from "./categoryTree";
import {
  activeCategoryId,
  applyDraftPrefill,
  setActiveCategoryId,
  today,
  type DraftProblem,
} from "./draft";
import {
  calcFromPaise,
  calcPaise,
  computePaise,
  EMPTY_CALC,
  paiseToEntry,
  parsePaise,
  type OperatorKey,
} from "./money";
import { RECENT_LIMIT, recentToPrefill } from "./RecentEntries";
import {
  PAYMENT_METHODS,
  accountLabel,
  type EntryAccount,
  type EntryCategory,
  type EntryRecord,
  type EntryType,
  type PaymentMethod,
  type SavedEntry,
} from "./types";
import { useEntryForm } from "./useEntryForm";
import "./desktop-entry.css";

export interface DesktopEntryProps {
  record?: EntryRecord;
  defaultType?: EntryType;
  cloneFrom?: EntryRecord;
  onSaved?: (saved: SavedEntry) => void;
  onClose: () => void;
}

const TYPES: { type: EntryType; label: string }[] = [
  { type: "expense", label: "Expense" },
  { type: "income", label: "Income" },
  { type: "transfer", label: "Transfer" },
];

const UNGROUPED = "__ungrouped";

const OPS: Record<string, OperatorKey> = { "+": "add", "-": "subtract", "*": "multiply", "/": "divide", "×": "multiply", "÷": "divide" };

/**
 * Amount text -> paise. Accepts "1,250.50", "₹450" and simple sums like
 * "450+120" or "1200/3", evaluated LEFT TO RIGHT in exact paise — the same
 * calculator semantics as the phone keypad (`money.ts::computePaise`).
 */
export function evaluateAmount(text: string): number | null {
  const cleaned = text.replace(/[₹,\s]/g, "");
  if (!cleaned) return null;
  const parts = cleaned.split(/([+\-*/×÷])/).filter((p) => p !== "");
  if (parts.length === 0) return null;
  let total = parsePaise(parts[0]);
  if (total === null) return null;
  for (let i = 1; i < parts.length; i += 2) {
    const op = OPS[parts[i]];
    const operand = parts[i + 1] === undefined ? null : parsePaise(parts[i + 1]);
    if (!op || operand === null) return null;
    const next = computePaise(total, op, operand);
    if (next === null) return null;
    total = next;
  }
  return total;
}

/** Paise -> "2,47,365" / "1,250.50" — Indian grouping for the amount field. */
export function formatAmountText(paise: number): string {
  const [int, dec] = paiseToEntry(paise).split(".");
  const neg = int.startsWith("-");
  const grouped = Number(neg ? int.slice(1) : int).toLocaleString("en-IN");
  return `${neg ? "-" : ""}${grouped}${dec ? `.${dec}` : ""}`;
}

/** `YYYY-MM-DD` + n days, in UTC so no timezone can shift the day. */
export function shiftDate(date: string, days: number): string {
  const d = new Date(`${date}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** `2026-09-26` -> `Sat, 26 Sep 2026`. */
export function dateCaption(date: string): string {
  const d = new Date(`${date}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return "";
  return `${WEEKDAYS[d.getUTCDay()]}, ${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
}

/**
 * Keyboard flow: every primary field carries `data-dxe-field`; Enter moves to
 * the next one in DOM order (so it follows the form as laid out for the
 * current type), and Enter on the LAST one saves.
 */
function fieldsIn(root: HTMLElement | null): HTMLElement[] {
  if (!root) return [];
  return [...root.querySelectorAll<HTMLElement>("[data-dxe-field]")].filter(
    (el) => !(el as HTMLInputElement).disabled,
  );
}

function yesterday(): string {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  return today(d);
}

function hintFor(problems: DraftProblem[]): string {
  if (problems.includes("amount")) return "Enter an amount";
  if (problems.includes("category")) return "Pick a category";
  if (problems.includes("sub-category")) return "Pick a sub-category";
  if (problems.includes("remark")) return "Add a remark";
  if (problems.includes("same-account")) return "A transfer needs two different accounts";
  if (problems.includes("from-account")) return "Choose the account it comes from";
  if (problems.includes("to-account")) return "Choose the account it goes to";
  return "Ready to save";
}

export function DesktopEntry({ record, cloneFrom, defaultType, onSaved, onClose }: DesktopEntryProps) {
  const form = useEntryForm({ record, cloneFrom, defaultType, onSaved });
  const {
    draft,
    setDraft,
    justSaved,
    setJustSaved,
    isEdit,
    categories,
    accounts,
    labels,
    topCategories,
    custom,
    save,
    recentEntries,
    categoryIsRemembered,
    repeatCandidatePaise,
    selectedCategory,
    rules,
    allProblems,
    problems,
    ready,
    update,
    onTypeChange,
    submit,
    applyTemplatePrefill,
    applyTemplatePrefillAndSave,
    repeatLastEntry,
    duplicate,
    duplicateCurrent,
  } = form;

  /* ── amount: free text, kept in step with the draft's calculator state ── */
  const draftPaise = calcPaise(draft.calc);
  const [amountText, setAmountText] = useState(() =>
    draftPaise !== null ? formatAmountText(draftPaise) : "",
  );
  const amountRef = useRef<HTMLInputElement>(null);
  const rootRef = useRef<HTMLDivElement>(null);

  /** Focus the field after `from`; past the last one, save. */
  function focusNext(from: HTMLElement) {
    const fields = fieldsIn(rootRef.current);
    const i = fields.indexOf(from);
    const next = i >= 0 ? fields[i + 1] : undefined;
    if (next) next.focus();
    else void saveFromKeyboard("close");
  }

  async function saveFromKeyboard(then: "close" | "again") {
    onAmountBlur();
    if (!ready) {
      form.setAttempted(true);
      return;
    }
    await submit(then);
    if (then === "again") amountRef.current?.focus();
  }

  function onDialogKeyDown(e: React.KeyboardEvent<HTMLDivElement>) {
    if (e.defaultPrevented) return;
    // Ctrl+Enter saves from anywhere; Ctrl+Shift+Enter saves and starts the next.
    if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      void saveFromKeyboard(e.shiftKey ? "again" : "close");
      return;
    }
    // Alt+1/2/3 switches Expense / Income / Transfer (not on an edit).
    if (e.altKey && !isEdit && ["1", "2", "3"].includes(e.key)) {
      e.preventDefault();
      onTypeChange(TYPES[Number(e.key) - 1].type);
      return;
    }
    // Plain Enter on a field: next field.
    const target = e.target as HTMLElement;
    if (e.key === "Enter" && !e.shiftKey && !e.altKey && target.hasAttribute("data-dxe-field")) {
      e.preventDefault();
      if (target === amountRef.current) onAmountBlur();
      focusNext(target);
    }
  }
  useEffect(() => {
    // A prefill (recent, template, clone, same-as-last, "add another") changed
    // the amount under the field — show it, unless the text already means it.
    if (evaluateAmount(amountText) !== draftPaise) {
      setAmountText(draftPaise !== null ? formatAmountText(draftPaise) : "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftPaise]);
  useEffect(() => {
    amountRef.current?.focus();
  }, []);

  function onAmountChange(text: string) {
    setAmountText(text);
    const paise = evaluateAmount(text);
    update({ calc: paise !== null ? calcFromPaise(paise) : EMPTY_CALC });
  }

  function onAmountBlur() {
    const paise = evaluateAmount(amountText);
    if (paise !== null) setAmountText(formatAmountText(paise));
  }

  const selectedId = activeCategoryId(draft);
  const liveAccounts = (current: string) =>
    accounts.filter((a) => !a.isArchived || a.id === current);

  return (
    <div
      ref={rootRef}
      className="dxe"
      role="dialog"
      aria-modal="true"
      aria-label={isEdit ? "Edit record" : "New record"}
      onKeyDown={onDialogKeyDown}
    >
      {/* ── title + type tabs ─────────────────────────────────────────── */}
      <div className="dxe-head">
        <h2>{isEdit ? "Edit Record" : "New Record"}</h2>
        {isEdit && (
          <span className="dxe-note">
            The type cannot change on an edit.
            {record?.externalSourceId && (
              <span className="ml-2 inline-flex items-center gap-1">
                <Link2 className="h-3 w-3" aria-hidden /> Synced from rent
              </span>
            )}
          </span>
        )}
        <button type="button" className="dxe-x" aria-label="Close" onClick={onClose}>
          <X className="h-4 w-4" />
        </button>
      </div>
      <div className="dxe-tabs" role="tablist" aria-label="Record type">
        {TYPES.map(({ type, label }) => (
          <button
            key={type}
            type="button"
            role="tab"
            aria-selected={draft.type === type}
            disabled={isEdit && draft.type !== type}
            className={`dxe-tab--${type}`}
            onClick={() => onTypeChange(type)}
            title={`Alt+${TYPES.findIndex((t) => t.type === type) + 1}`}
          >
            {label}
          </button>
        ))}
      </div>

      <div className="dxe-body">
        {/* ── the form ────────────────────────────────────────────────── */}
        <div className="dxe-form">
          <Row label="Amount" required invalid={problems.includes("amount")}>
            <div className={cn("dxe-amount", `dxe-amount--${draft.type}`)}>
              <span aria-hidden>₹</span>
              <input
                ref={amountRef}
                value={amountText}
                onChange={(e) => onAmountChange(e.target.value)}
                onBlur={onAmountBlur}
                data-dxe-field="amount"
                inputMode="decimal"
                placeholder="0"
                aria-label="Amount"
                aria-invalid={problems.includes("amount") || undefined}
              />
              {/[+\-*/×÷]/.test(amountText.replace(/^-/, "")) && draftPaise !== null && (
                <span className="dxe-amount-eq">
                  = <AmountText paise={draftPaise} />
                </span>
              )}
            </div>
          </Row>

          <Row label="Date" required>
            <div className="flex flex-wrap items-center gap-2">
              <div className="dxe-date">
                <button
                  type="button"
                  tabIndex={-1}
                  aria-label="Previous day"
                  title="Previous day ( - )"
                  onClick={() => update({ date: shiftDate(draft.date, -1) })}
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <input
                  type="date"
                  value={draft.date}
                  onChange={(e) => update({ date: e.target.value || today() })}
                  onKeyDown={(e) => {
                    // + / - step a day; T today; Y yesterday. The native
                    // field still takes typed digits and arrow keys per part.
                    const step = ({ "+": 1, "=": 1, "-": -1, _: -1 } as Record<string, number>)[e.key];
                    if (step) {
                      e.preventDefault();
                      update({ date: shiftDate(draft.date, step) });
                    } else if (e.key === "t" || e.key === "T") {
                      e.preventDefault();
                      update({ date: today() });
                    } else if (e.key === "y" || e.key === "Y") {
                      e.preventDefault();
                      update({ date: yesterday() });
                    }
                  }}
                  data-dxe-field="date"
                  aria-label="Record date"
                />
                <button
                  type="button"
                  tabIndex={-1}
                  aria-label="Next day"
                  title="Next day ( + )"
                  onClick={() => update({ date: shiftDate(draft.date, 1) })}
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
              <span className="dxe-date-cap">{dateCaption(draft.date)}</span>
              <button
                type="button"
                tabIndex={-1}
                className="dxe-chip"
                aria-pressed={draft.date === today()}
                onClick={() => update({ date: today() })}
                title="T"
              >
                Today
              </button>
              <button
                type="button"
                tabIndex={-1}
                className="dxe-chip"
                aria-pressed={draft.date === yesterday()}
                onClick={() => update({ date: yesterday() })}
                title="Y"
              >
                Yesterday
              </button>
            </div>
          </Row>

          {draft.type === "transfer" ? (
            <>
              <Row label="From account" required invalid={problems.includes("from-account")}>
                <AccountSelect
                  field
                  label="From account"
                  accounts={liveAccounts(draft.fromAccountId).filter((a) => a.id !== draft.toAccountId)}
                  value={draft.fromAccountId}
                  onChange={(id) => update({ fromAccountId: id })}
                />
              </Row>
              <Row
                label="To account"
                required
                invalid={problems.includes("to-account") || problems.includes("same-account")}
              >
                <AccountSelect
                  field
                  label="To account"
                  accounts={liveAccounts(draft.toAccountId).filter((a) => a.id !== draft.fromAccountId)}
                  value={draft.toAccountId}
                  onChange={(id) => update({ toAccountId: id })}
                />
              </Row>
            </>
          ) : (
            <>
              <Row label="Account" invalid={problems.includes("account")}>
                <AccountSelect
                  field
                  label="Account"
                  accounts={liveAccounts(draft.accountId)}
                  value={draft.accountId}
                  onChange={(id) => update({ accountId: id })}
                  allowNone
                />
              </Row>

              <Row
                label="Category"
                required
                invalid={problems.includes("category") || problems.includes("sub-category")}
                top
              >
                <CategoryPicker
                  categories={categories}
                  selectedId={selectedId}
                  allowParent={!rules}
                  remembered={categoryIsRemembered}
                  invalid={problems.includes("category") || problems.includes("sub-category")}
                  onSelect={(id) => {
                    setDraft((current) => setActiveCategoryId(current, id));
                    setJustSaved(null);
                  }}
                  onPicked={(searchBox) => focusNext(searchBox)}
                />
              </Row>

              {draft.type === "expense" && (
                <Row label="Paid with">
                  <select
                    className="dxe-input w-[220px]"
                    value={draft.paymentMethod}
                    onChange={(e) => update({ paymentMethod: e.target.value as PaymentMethod })}
                    data-dxe-field="paid-with"
                    aria-label="Paid with"
                  >
                    {PAYMENT_METHODS.map((m) => (
                      <option key={m.value} value={m.value}>
                        {m.label}
                      </option>
                    ))}
                  </select>
                </Row>
              )}
            </>
          )}

          <Row
            label="Remark"
            required={!isEdit && draft.type !== "transfer"}
            invalid={problems.includes("remark")}
          >
            <input
              className="dxe-input w-full"
              value={draft.note}
              onChange={(e) => update({ note: e.target.value })}
              maxLength={500}
              placeholder="What it was for, e.g. Salary Sep of 2026"
              data-dxe-field="remark"
              aria-label="Remark"
              aria-invalid={problems.includes("remark") || undefined}
            />
          </Row>

          {labels.length > 0 && (
            <Row label="Labels" top>
              <LabelChips
                labels={labels}
                selectedIds={draft.labelIds}
                onChange={(labelIds) => update({ labelIds })}
              />
            </Row>
          )}

          <div className="dxe-extra">
            <ExtraFields state={custom} />
          </div>

          {problems.includes("same-account") && (
            <p className="dxe-err">A transfer needs two different accounts.</p>
          )}
        </div>

        {/* ── shortcuts ───────────────────────────────────────────────── */}
        <aside className="dxe-side">
          {!isEdit && !draft.calc.entry && !draft.note && repeatCandidatePaise !== null && (
            <button type="button" className="dxe-repeat" onClick={repeatLastEntry}>
              <RotateCcw className="h-3.5 w-3.5" aria-hidden />
              Same as last · <AmountText paise={repeatCandidatePaise} />
            </button>
          )}

          {draft.type !== "transfer" && !isEdit && !cloneFrom && recentEntries.length > 0 && (
            <section>
              <h3>Recent</h3>
              <ul className="dxe-list" aria-label="Recent entries">
                {recentEntries.slice(0, RECENT_LIMIT).map((entry) => (
                  <li key={`${entry.description}|${entry.categoryId}`}>
                    <button
                      type="button"
                      aria-label={`Use recent entry: ${entry.description}`}
                      title={`${entry.category.name} · used ${entry.count}×, last ${entry.lastDate}`}
                      onClick={() => {
                        setDraft((current) => applyDraftPrefill(current, recentToPrefill(entry)));
                        setJustSaved(null);
                      }}
                    >
                      <span
                        className="dxe-dot"
                        style={{ backgroundColor: entry.category.color ?? "var(--cp-ink-3)" }}
                        aria-hidden
                      />
                      <span className="min-w-0 flex-1">
                        <span className="block truncate">{entry.description}</span>
                        <span className="block truncate text-[11.5px] text-[var(--cp-ink-3)]">
                          {entry.category.name}
                        </span>
                      </span>
                      <AmountText paise={entry.amountPaise} className="shrink-0 text-[12.5px]" />
                    </button>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {draft.type !== "transfer" && topCategories.length > 0 && (
            <section>
              <h3>Most used</h3>
              <div className="flex flex-wrap gap-1.5">
                {topCategories.map((tc) => (
                  <button
                    key={tc.categoryId}
                    type="button"
                    className="dxe-chip"
                    aria-pressed={selectedId === tc.categoryId}
                    onClick={() => {
                      setDraft((current) => setActiveCategoryId(current, tc.categoryId));
                      setJustSaved(null);
                    }}
                  >
                    <span
                      className="dxe-dot"
                      style={{ backgroundColor: tc.color ?? "var(--cp-ink-3)" }}
                      aria-hidden
                    />
                    {tc.name}
                  </button>
                ))}
              </div>
            </section>
          )}

          {draft.type !== "transfer" && (
            <section>
              {/* The picker carries its own "Templates" heading. */}
              <TemplatePicker
                type={draft.type}
                onApply={applyTemplatePrefill}
                onApplyAndSave={isEdit ? undefined : applyTemplatePrefillAndSave}
                isSaving={save.isPending}
              />
            </section>
          )}
        </aside>
      </div>

      {justSaved && (
        <div className="dxe-saved">
          <Check className="h-4 w-4 text-[var(--cp-emerald)]" aria-hidden />
          Saved <AmountText paise={justSaved.amountPaise} className="font-bold" />
          <span className="flex-1" />
          <button type="button" className="dxe-btn" onClick={() => duplicate(justSaved)}>
            <Copy className="h-3.5 w-3.5" aria-hidden /> Duplicate
          </button>
          <button type="button" className="dxe-btn" onClick={onClose}>
            Done
          </button>
        </div>
      )}

      {/* ── save bar ──────────────────────────────────────────────────── */}
      <div className="dxe-keys" aria-label="Keyboard shortcuts">
        <span><kbd>Enter</kbd> next field</span>
        <span><kbd>Ctrl</kbd>+<kbd>Enter</kbd> save</span>
        <span><kbd>Ctrl</kbd>+<kbd>Shift</kbd>+<kbd>Enter</kbd> save &amp; add another</span>
        <span><kbd>Alt</kbd>+<kbd>1</kbd>/<kbd>2</kbd>/<kbd>3</kbd> type</span>
        <span>Date: <kbd>+</kbd>/<kbd>-</kbd> day · <kbd>T</kbd> today · <kbd>Y</kbd> yesterday</span>
        <span>Category: type, <kbd>↑</kbd><kbd>↓</kbd>, <kbd>Enter</kbd></span>
        <span><kbd>Esc</kbd> close</span>
      </div>
      <div className="dxe-foot">
        <span className="dxe-hint">
          {selectedCategory && draft.type !== "transfer"
            ? `${categoryPath(selectedCategory, categories)} · `
            : ""}
          {hintFor(allProblems)}
        </span>
        {isEdit && (
          <button type="button" className="dxe-btn" onClick={duplicateCurrent}>
            <Copy className="h-3.5 w-3.5" aria-hidden /> Duplicate
          </button>
        )}
        <button type="button" className="dxe-btn" onClick={onClose}>
          Cancel
        </button>
        <button
          type="button"
          className="dxe-btn"
          disabled={!ready}
          onClick={() => void saveFromKeyboard("again")}
        >
          <Plus className="h-3.5 w-3.5" aria-hidden /> Save &amp; add another
        </button>
        <button
          type="button"
          className="dxe-btn dxe-btn--primary"
          disabled={!ready}
          onClick={() => void submit("close")}
        >
          {save.isPending ? "Saving…" : isEdit ? "Update" : "Save"}
        </button>
      </div>
    </div>
  );
}

function Row({
  label,
  required,
  invalid,
  top,
  children,
}: {
  label: string;
  required?: boolean;
  invalid?: boolean;
  /** Align the label to the top (tall fields: category, labels). */
  top?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className={cn("dxe-row", top && "dxe-row--top", invalid && "dxe-row--bad")}>
      <span className="dxe-label">
        {label}
        {required && <b aria-hidden> *</b>}
      </span>
      <span className="dxe-colon" aria-hidden>
        :
      </span>
      <div className="min-w-0">{children}</div>
    </div>
  );
}

function AccountSelect({
  field,
  label,
  accounts,
  value,
  onChange,
  allowNone,
}: {
  /** Part of the Enter-to-next keyboard flow. */
  field?: boolean;
  label: string;
  accounts: EntryAccount[];
  value: string;
  onChange(id: string): void;
  allowNone?: boolean;
}) {
  const { user } = useAuth();
  const { bankShort } = useHousehold();
  // The same Bank/Type-User name the Accounts tiles use (these are the
  // signed-in user's own accounts).
  const nameOf = (a: EntryAccount) =>
    a.accountType
      ? accountTileName(a.bankName, a.accountType, user?.name ?? "", bankShort?.(a.bankName))
      : accountLabel(a);
  // Spending accounts first, deposits (FD/RD) after, archived last — so
  // typing "Kotak" in the closed dropdown lands on Kotak/Savings, not the FD.
  const rank = (a: EntryAccount) =>
    a.isArchived ? 2 : ["FD", "RD"].includes((a.accountType ?? "").toUpperCase()) ? 1 : 0;
  const sorted = [...accounts].sort(
    (a, b) => rank(a) - rank(b) || nameOf(a).localeCompare(nameOf(b)),
  );
  return (
    <select
      className="dxe-input w-[320px]"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      data-dxe-field={field ? "account" : undefined}
      aria-label={label}
    >
      <option value="">{allowNone ? "No account" : "Choose account…"}</option>
      {sorted.map((a) => (
        <option key={a.id} value={a.id}>
          {nameOf(a)}
          {a.isArchived ? " (archived)" : ""}
        </option>
      ))}
    </select>
  );
}

/**
 * Two-pane category picker: parents on the left, the focused parent's
 * sub-categories on the right, a search box that flattens both into
 * "Parent › Child" matches. On a NEW entry a parent that has sub-categories
 * is not itself selectable (F7 — pick a sub-category); a parent with none is.
 */
export function CategoryPicker({
  categories,
  selectedId,
  allowParent,
  remembered,
  invalid,
  onSelect,
  onPicked,
}: {
  /** A keyboard pick finished — the host moves focus on (gets the search box). */
  onPicked?(searchBox: HTMLElement): void;
  categories: EntryCategory[];
  selectedId: string | null;
  /** Edits may keep a parent-level category (F7 applies to new entries). */
  allowParent: boolean;
  remembered?: boolean;
  invalid?: boolean;
  onSelect(id: string): void;
}) {
  const offered = useMemo(
    () => selectableCategories(categories, selectedId),
    [categories, selectedId],
  );
  const groups = useMemo(() => groupCategories(offered), [offered]);
  const selected = offered.find((c) => c.id === selectedId) ?? null;
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(0);
  // The synthetic bucket (rows whose parent is not offered) has no parent
  // row, so it is keyed by a sentinel rather than by null.
  const keyOf = (g: (typeof groups)[number]) => g.parent?.id ?? UNGROUPED;
  const groupKeyOfSelection = () => {
    if (!selectedId) return null;
    const g = groups.find(
      (x) => x.parent?.id === selectedId || x.children.some((c) => c.id === selectedId),
    );
    return g ? keyOf(g) : null;
  };
  const [focusId, setFocusId] = useState<string | null>(groupKeyOfSelection);
  useEffect(() => {
    const key = groupKeyOfSelection();
    if (key) setFocusId(key);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId, groups]);

  const focused =
    groups.find((g) => keyOf(g) === focusId) ?? groups[0] ?? null;

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    const out: { id: string; path: string }[] = [];
    for (const g of groups) {
      const parentHit = g.parent?.name.toLowerCase().includes(q) ?? false;
      if (g.parent && (g.children.length === 0 || allowParent) && parentHit) {
        out.push({ id: g.parent.id, path: g.parent.name });
      }
      for (const c of g.children) {
        if (parentHit || c.name.toLowerCase().includes(q)) {
          out.push({ id: c.id, path: g.parent ? `${g.parent.name} › ${c.name}` : c.name });
        }
      }
    }
    return out;
  }, [groups, query, allowParent]);

  return (
    <div className={cn("dxe-cat", invalid && "dxe-cat--bad")}>
      <div className="dxe-cat-top">
        <div className="dxe-cat-search">
          <Search className="h-3.5 w-3.5 text-[var(--cp-ink-3)]" aria-hidden />
          <input
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setActive(0);
            }}
            onKeyDown={(e) => {
              if (e.key === "ArrowDown" && matches.length) {
                e.preventDefault();
                setActive((i) => Math.min(i + 1, matches.length - 1));
              } else if (e.key === "ArrowUp" && matches.length) {
                e.preventDefault();
                setActive((i) => Math.max(i - 1, 0));
              } else if (e.key === "Enter" && !e.ctrlKey && !e.metaKey) {
                const pick = matches[active] ?? matches[0];
                if (pick) {
                  e.preventDefault();
                  onSelect(pick.id);
                  setQuery("");
                  setActive(0);
                  onPicked?.(e.currentTarget);
                } else if (!query.trim() && !selectedId) {
                  // Nothing typed and nothing chosen: stay here.
                  e.preventDefault();
                }
              } else if (e.key === "Escape" && query) {
                e.preventDefault();
                e.stopPropagation();
                setQuery("");
              }
            }}
            data-dxe-field="category"
            placeholder="Type to search categories…"
            aria-label="Search categories"
          />
        </div>
        <span className="dxe-cat-sel">
          {selected ? (
            <>
              <span
                className="dxe-dot"
                style={{ backgroundColor: selected.color ?? "var(--cp-ink-3)" }}
                aria-hidden
              />
              {categoryPath(selected, categories)}
              {remembered && <em> · last used</em>}
            </>
          ) : (
            <em>None selected</em>
          )}
        </span>
      </div>

      {query.trim() ? (
        <ul className="dxe-cat-results" role="listbox" aria-label="Matching categories">
          {matches.length === 0 && <li className="dxe-cat-empty">No category matches “{query}”.</li>}
          {matches.map((m, i) => (
            <li key={m.id}>
              <button
                type="button"
                role="option"
                tabIndex={-1}
                data-active={i === active || undefined}
                aria-selected={m.id === selectedId}
                onClick={() => {
                  onSelect(m.id);
                  setQuery("");
                }}
              >
                {m.path}
              </button>
            </li>
          ))}
        </ul>
      ) : (
        <div className="dxe-cat-panes">
          <ul role="listbox" aria-label="Categories">
            {groups.map((g) => {
              const id = keyOf(g);
              const isFocused = focused === g;
              const holdsSelection =
                (g.parent && g.parent.id === selectedId) || g.children.some((c) => c.id === selectedId);
              return (
                <li key={id}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={Boolean(holdsSelection)}
                    data-focused={isFocused || undefined}
                    onClick={() => {
                      setFocusId(id);
                      if (g.parent && g.children.length === 0) onSelect(g.parent.id);
                    }}
                  >
                    <span
                      className="dxe-dot"
                      style={{ backgroundColor: g.parent?.color ?? "var(--cp-ink-3)" }}
                      aria-hidden
                    />
                    <span className="min-w-0 flex-1 truncate">{g.parent?.name ?? "Ungrouped"}</span>
                    {g.children.length > 0 && <span className="dxe-cat-n">{g.children.length} ›</span>}
                  </button>
                </li>
              );
            })}
          </ul>
          <ul role="listbox" aria-label={focused?.parent ? `${focused.parent.name} sub-categories` : "Sub-categories"}>
            {focused && focused.children.length === 0 && (
              <li className="dxe-cat-empty">
                {focused.parent ? `${focused.parent.name} has no sub-categories.` : "Nothing here."}
              </li>
            )}
            {focused?.parent && focused.children.length > 0 && allowParent && (
              <li>
                <button
                  type="button"
                  role="option"
                  aria-selected={focused.parent.id === selectedId}
                  onClick={() => onSelect(focused.parent!.id)}
                >
                  <em>{focused.parent.name} (no sub-category)</em>
                </button>
              </li>
            )}
            {focused?.children.map((c) => (
              <li key={c.id}>
                <button
                  type="button"
                  role="option"
                  aria-selected={c.id === selectedId}
                  onClick={() => onSelect(c.id)}
                >
                  <span className="min-w-0 flex-1 truncate">{c.name}</span>
                  {c.id === selectedId && <Check className="h-3.5 w-3.5" aria-hidden />}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
